import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {MODEL_INPUT_TYPE} from 'model-hub/scientific-models/data-first/CustomEntityInputTypeDefinitions';
import {
  MODEL_CATEGORY,
  ScientificModelNode,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';

/**
 * Strict leaf types keep unrelated object columns (containers, files, ...) from matching TEXT. The backend
 * decides each entity cell's component type from its ID prefix, so SEQUENCE is allowed for AA-sequence
 * columns that are typed as DNA sequences, and non-protein IDs are rejected at run time. Biologics Protein
 * columns are typed as custom entities or, when built from the domain graph, as generic bioentities.
 */
function inputColumnSpec(
  allowedLeafTypes: ReadonlyArray<LeafType>
): GetPipelineConfigSpecType<'columnReference'> {
  return {
    kind: 'columnReference',
    required: true,
    inputDataName: 'inputDataset',
    allowMissingColumn: false,
    validationOptions: {
      allowedLeafTypes,
      strictLeafType: true,
      listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    },
  };
}

const AA_SEQUENCE_COLUMN = inputColumnSpec([LeafType.TEXT, LeafType.PROTEIN, LeafType.SEQUENCE]);
const AA_SEQUENCE_OR_BIOLOGICS_PROTEIN_COLUMN = inputColumnSpec([
  LeafType.TEXT,
  LeafType.PROTEIN,
  LeafType.SEQUENCE,
  LeafType.BASIC_FOLDER_ITEM,
  LeafType.BIOENTITY,
]);
const MOLECULE_COLUMN = inputColumnSpec([LeafType.TEXT, LeafType.MOLECULE]);

const AA_SEQUENCE_INFO_TEXT = 'Text sequences or AA sequences';
const MOLECULE_INFO_TEXT = 'SMILES strings or molecules';

export interface ModelInputField {
  key: string;
  displayName: string;
  infoText?: string;
  spec: PipelineConfigJsonSpec;
}

/** Keep in sync with scientific_model_job_config_builder.py (`USE_MSA`). */
export const USE_MSA_KEY = 'useMsa';
export const USE_MSA_DISPLAY_NAME = 'Use MSAs';

// Keep in sync with scientific_model_job_config_builder.py, which reads these keys for the same
// model capabilities.
export function getModelInputFields(model: ScientificModelNode): ReadonlyArray<ModelInputField> | null {
  if (model.category === MODEL_CATEGORY.PROPERTY_PREDICTION) {
    if (model.inputTypes.includes(MODEL_INPUT_TYPE.ANTIBODY)) {
      return [
        {
          key: 'heavyChainColumn',
          displayName: 'Heavy-chain column',
          infoText: AA_SEQUENCE_INFO_TEXT,
          spec: AA_SEQUENCE_COLUMN,
        },
        {
          key: 'lightChainColumn',
          displayName: 'Light-chain column',
          infoText: AA_SEQUENCE_INFO_TEXT,
          spec: AA_SEQUENCE_COLUMN,
        },
      ];
    }
    if (model.inputTypes.includes(MODEL_INPUT_TYPE.MOLECULE)) {
      return [
        {
          key: 'smilesColumn',
          displayName: 'Molecule column',
          infoText: MOLECULE_INFO_TEXT,
          spec: MOLECULE_COLUMN,
        },
      ];
    }
    return null;
  }
  if (model.category === MODEL_CATEGORY.STRUCTURE_PREDICTION) {
    return getStructurePredictionInputFields(model);
  }
  return null;
}

function getStructurePredictionInputFields(model: ScientificModelNode): ReadonlyArray<ModelInputField> {
  const {supportsMultimers, supportsMultimerMolecules} = model.capabilities;
  const fields: Array<ModelInputField> = [
    {
      key: 'sequenceColumns',
      displayName: supportsMultimers ? 'Protein columns' : 'Protein column',
      infoText: supportsMultimers
        ? 'Text sequences, AA sequences, or Biologics Proteins'
        : AA_SEQUENCE_INFO_TEXT,
      spec: {
        kind: 'array',
        required: true,
        elementSpec: supportsMultimers ? AA_SEQUENCE_OR_BIOLOGICS_PROTEIN_COLUMN : AA_SEQUENCE_COLUMN,
        minItems: 1,
        ...(supportsMultimers ? {} : {maxItems: 1}),
      },
    },
  ];
  if (supportsMultimers && supportsMultimerMolecules) {
    fields.push({
      key: 'ligandColumns',
      displayName: 'Ligand columns',
      infoText: MOLECULE_INFO_TEXT,
      spec: {kind: 'array', required: false, elementSpec: MOLECULE_COLUMN},
    });
  }
  if (supportsMSA(model)) {
    fields.push({
      key: USE_MSA_KEY,
      displayName: USE_MSA_DISPLAY_NAME,
      spec: {kind: 'boolean', required: false},
    });
  }
  return fields;
}
